import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'store/modal';
import apiEventEmitter from 'core/EventEmitter';
import API from 'core/Api';

interface VersionCheckProps {
  type: 'confirm';
  heading: string;
  content: string;
  onConfirm: () => void;
  confirmLabel: string;
  cancelLabel: string;
}

const useVersionCheck = () => {
  const { showModal } = useModal();
  const { t } = useTranslation();
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState<boolean>(() => !navigator.onLine);
  const [lastServerErrorAt, setLastServerErrorAt] = useState<number | null>(null);
  const [lastStatus, setLastStatus] = useState<'offline' | 'server' | null>(null);

  // Create stable function references to prevent useEffect re-execution
  const checkVersionRef = useRef<() => Promise<void>>();
  const clearMessageRef = useRef<() => void>();
  const emitMessageRef = useRef<(kind: 'offline' | 'server') => void>();

  // Shared promise to avoid multiple simultaneous public fetches
  let publicFetchPromise: Promise<boolean> | null = null;

  function checkInternetConnectivity(): Promise<boolean> {
    if (!publicFetchPromise) {
      // Change to a Cloudflare IP, so we are not dependent on google services
      // as we don't use them for other stuff
      publicFetchPromise = fetch('https://ehr.bg/cdn-cgi/trace', {
        method: 'GET',
        mode: 'no-cors',
      })
        .then(() => true)
        .catch(() => false)
        .finally(() => {
          // Reset after completion so future errors can re-check
          publicFetchPromise = null;
        });
    }
    return publicFetchPromise;
  }

  function isTooLargeFileError(error: any): boolean {
    const statusCode = error?.response?.status || error?.status || error?.request?.status;
    const message = String(error?.message || '').toLowerCase();

    return statusCode === 413 || message.includes('413');
  }

  const showUpdateModal = useCallback(
    (newVersion: string) => {
      const modalProps: VersionCheckProps = {
        type: 'confirm',
        heading: t('newVersionAvailable.title'),
        content: t('newVersionAvailable.description'),
        onConfirm: () => {
          localStorage.setItem('ehr_fe_version', newVersion);
          window.location.reload();
        },
        confirmLabel: t('newVersionAvailable.refresh'),
        cancelLabel: t('newVersionAvailable.later'),
      };

      showModal(modalProps);
    },
    [showModal, t]
  );

  const emitMessage = useCallback(
    (kind: 'offline' | 'server') => {
      if (kind === 'offline') {
        if (lastStatus !== 'offline') {
          apiEventEmitter.emit('api-error', t('errors.noConnection'));
          setLastStatus('offline');
        }
      } else if (kind === 'server') {
        // Only emit server error if we are not currently offline
        if (lastStatus !== 'server') {
          apiEventEmitter.emit('api-error', t('errors.serverUnavailable'));
          setLastStatus('server');
        }
      }
    },
    [t, lastStatus]
  );

  const clearMessage = useCallback(() => {
    // Only clear error toast, not message toast
    apiEventEmitter.emit('api-error-clear');
    if (lastStatus !== null) {
      setLastStatus(null);
    }
  }, [lastStatus]);

  // Update refs when functions change
  clearMessageRef.current = clearMessage;
  emitMessageRef.current = emitMessage;

  const checkVersion = useCallback(async () => {
    if (isOffline) return; // Suppress server calls while clearly offline
    try {
      const response = await API.get<{ fe_version: string; message?: string }>('/health-check');
      const serverVersion = response.data.fe_version;
      const serverMessage = response.data.message;

      // Clear any previous offline/server-down error on successful call
      clearMessage();

      // Always clear health-check message if a new one is present
      if (serverMessage) {
        apiEventEmitter.emit('api-message-clear');
      }

      // Update offline state if we were offline but now have connection
      if (isOffline) {
        setIsOffline(false);
      }

      if (serverMessage) {
        apiEventEmitter.emit('api-message', serverMessage);
      }

      if (!currentVersion) {
        setCurrentVersion(serverVersion);
        localStorage.setItem('ehr_fe_version', serverVersion);
      } else if (currentVersion !== serverVersion) {
        showUpdateModal(serverVersion);
        setCurrentVersion(serverVersion);
        localStorage.setItem('ehr_fe_version', serverVersion);
      }
    } catch (error: any) {
      if (isTooLargeFileError(error)) {
        return;
      }
      console.log('error', error);
      if (error.response) {
        emitMessage('server');
      } else {
        // No response: network/client error (offline, DNS, CORS, etc)
        const isOnline = await checkInternetConnectivity();
        if (isOnline) {
          if (isOffline) {
            setIsOffline(false);
          }
          emitMessage('server');
        } else {
          if (!isOffline) {
            setIsOffline(true);
          }
          emitMessage('offline');
        }
      }
      setLastServerErrorAt(Date.now());
    }
  }, [currentVersion, showUpdateModal, isOffline, emitMessage, clearMessage]);

  // Update checkVersion ref after it's defined
  checkVersionRef.current = checkVersion;

  const startVersionCheck = useCallback(() => {
    const healthCheckIntervalMinutes = Number(process.env.REACT_APP_HEALTH_CHECK_INTERVAL_MINUTES) || 5; // default 5
    const healthCheckIntervalMs = healthCheckIntervalMinutes * 60 * 1000;

    checkVersion(); // Initial attempt

    const intervalId = setInterval(checkVersion, healthCheckIntervalMs);
    return () => clearInterval(intervalId); // Cleanup
  }, [checkVersion]);

  // Initialize version checking only once on mount
  useEffect(() => {
    const cleanupInterval = startVersionCheck();
    return cleanupInterval;
  }, []); // Empty dependency array - only run once on mount

  // Handle online/offline events and API errors separately
  useEffect(() => {
    const handleOnline = () => {
      if (isOffline) {
        setIsOffline(false);
        clearMessageRef.current?.();
        // Force clear any lingering error toasts
        apiEventEmitter.emit('api-error-clear');
        // Do not clear api-message here, let it persist
        checkVersionRef.current?.(); // Re-check immediately when back online
      }
    };
    const handleOffline = () => {
      if (!isOffline) {
        setIsOffline(true);
        emitMessageRef.current?.('offline');
      }
    };

    // Listen for connection errors from any API call and handle them centrally
    const handleApiError = (error: any) => {
      if (isTooLargeFileError(error)) {
        return;
      }

      if (error && error.response) {
        // Server responded with an error (4xx, 5xx, CORS preflight failure, etc)
        emitMessageRef.current?.('server');
        setLastServerErrorAt(Date.now());
        return;
      }
      // No response: network/client error (offline, DNS, CORS, etc)
      checkInternetConnectivity().then(isOnline => {
        if (isOnline) {
          if (isOffline) {
            setIsOffline(false);
          }
          emitMessageRef.current?.('server');
        } else {
          if (!isOffline) {
            setIsOffline(true);
          }
          emitMessageRef.current?.('offline');
        }
        setLastServerErrorAt(Date.now());
      });
    };

    // Listen for successful API responses to clear error messages
    const handleApiSuccess = () => {
      // Always clear error toasts when API calls succeed (connection is working)
      clearMessageRef.current?.();
      // Do not clear api-message here, let it persist

      // Update offline state if we were offline but now have connection
      if (isOffline && navigator.onLine) {
        setIsOffline(false);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    apiEventEmitter.on('api-connection-error', handleApiError);
    apiEventEmitter.on('api-success', handleApiSuccess);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      apiEventEmitter.off('api-connection-error', handleApiError);
      apiEventEmitter.off('api-success', handleApiSuccess);
    };
  }, [isOffline]); // Only depend on isOffline to avoid re-initialization

  return { isOffline, lastServerErrorAt, retry: checkVersion };
};

export default useVersionCheck;
